import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Select, 
  Space, 
  Typography,
  Alert,
  Card,
  DatePicker,
  TimePicker,
  Switch,
  Tag,
  Progress,
  List,
  Tooltip,
  message,
  Tabs
} from 'antd';
import { 
  MessageOutlined,
  SendOutlined,
  ScheduleOutlined,
  SaveOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import moment from 'moment';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

/**
 * AdvancedSMSComposer - Enhanced SMS composition with templates, scheduling, and tracking
 */
const AdvancedSMSComposer = ({ 
  visible, 
  onClose, 
  selectedSites = [],
  crisisId = null,
  onSendComplete 
}) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [form] = Form.useForm();
  
  // State management
  const [activeTab, setActiveTab] = useState('compose');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState(null);
  const [sendResults, setSendResults] = useState(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [deliveryTracking, setDeliveryTracking] = useState(true);
  const [usePersonalization, setUsePersonalization] = useState(true);

  // SMS Templates with categories
  const DEFAULT_TEMPLATES = {
    emergency: {
      name: 'Emergency Response',
      templates: [
        {
          id: 'emergency_evacuation',
          name: 'Evacuation Notice',
          message: 'URGENT EVACUATION: Immediate evacuation required at {site_name}. Follow your emergency procedures. Confirm evacuation completion by replying SAFE. Time: {timestamp}',
          category: 'emergency',
          variables: ['site_name', 'timestamp']
        },
        {
          id: 'emergency_safety',
          name: 'Safety Check',
          message: 'This is a site security/safety check from {organisation_name} Crisis Management. Please confirm your site\'s operational status:\n1 = Active\n2 = Degraded\n3 = Evacuating\n4 = Evacuated\n5 = Closed and Secure',
          category: 'emergency',
          variables: ['organisation_name']
        }
      ]
    },
    operational: {
      name: 'Operational Updates',
      templates: [
        {
          id: 'ops_status_request',
          name: 'Status Request',
          message: 'Hi {manager_name}, please provide current operational status for {site_name}. Include staff count, any issues, and expected closure time if applicable.',
          category: 'operational',
          variables: ['manager_name', 'site_name']
        },
        {
          id: 'ops_closure_notice',
          name: 'Site Closure',
          message: 'Notice: {site_name} will be temporarily closed due to current situation. Please secure the site and ensure all staff are safely evacuated.',
          category: 'operational',
          variables: ['site_name']
        }
      ]
    }
  };

  // Initialize recipients from selected sites
  useEffect(() => {
    if (visible && selectedSites.length > 0) {
      const siteRecipients = selectedSites
        .filter(site => site.site_manager_mobile && site.site_manager_name)
        .map(site => ({
          id: site.id,
          name: site.site_manager_name,
          mobile: site.site_manager_mobile,
          email: site.site_manager_email,
          siteName: site.site_name,
          siteType: site.site_type,
          selected: true,
          deliveryStatus: 'pending'
        }));
      
      setRecipients(siteRecipients);
      setActiveTab('compose');
      setSendResults(null);
      form.resetFields();
    }
  }, [visible, selectedSites, form]);

  // Update character count and cost estimation
  useEffect(() => {
    const processedMessage = processMessageText(messageText);
    setCharacterCount(processedMessage.length);
    
    // Estimate cost (typical SMS pricing: $0.01-0.05 per message)
    const messageCount = Math.ceil(processedMessage.length / 160);
    const recipientCount = recipients.filter(r => r.selected).length;
    setEstimatedCost(messageCount * recipientCount * 0.02); // $0.02 per SMS segment
  }, [messageText, recipients]);

  const processMessageText = (text) => {
    if (!usePersonalization) return text;
    
    let processed = text;
    
    // Replace common variables
    processed = processed.replace(/{timestamp}/g, moment().format('DD/MM/YYYY HH:mm'));
    processed = processed.replace(/{current_time}/g, moment().format('HH:mm'));
    processed = processed.replace(/{current_date}/g, moment().format('DD/MM/YYYY'));
    
    return processed;
  };

  const handleTemplateSelect = async (templateId) => {
    let template = null;
    
    // Find in default templates
    Object.values(DEFAULT_TEMPLATES).forEach(category => {
      const found = category.templates.find(t => t.id === templateId);
      if (found) template = found;
    });
    
    if (template) {
      let processedMessage = template.message;
      
      // Replace organization name in template display
      if (template.message.includes('{organisation_name}')) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { db } = await import('../../config/firebase');
          const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const orgName = orgData.name || orgData.companyName || 'Your Organization';
            processedMessage = template.message.replace(/{organisation_name}/g, orgName);
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch organization name for template display:', error);
          processedMessage = template.message.replace(/{organisation_name}/g, 'Your Organization');
        }
      }
      
      setSelectedTemplate(template);
      setMessageText(processedMessage);
      form.setFieldsValue({ message: processedMessage });
    }
  };

  const saveAsTemplate = async () => {
    if (!messageText.trim()) {
      message.error('Please enter a message to save as template');
      return;
    }

    try {
      const templateData = {
        name: `Custom Template ${templates.length + 1}`,
        message: messageText,
        category: 'custom',
        orgId: currentUser.orgId,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        variables: extractVariables(messageText)
      };

      await addDoc(collection(db, 'c3_sms_templates'), templateData);
      message.success('Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const extractVariables = (text) => {
    const regex = /{([^}]+)}/g;
    const variables = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    
    return variables;
  };

  const handleRecipientToggle = (recipientId, checked) => {
    setRecipients(prev => 
      prev.map(recipient => 
        recipient.id === recipientId 
          ? { ...recipient, selected: checked }
          : recipient
      )
    );
  };

  const selectAllRecipients = (checked) => {
    setRecipients(prev => 
      prev.map(recipient => ({ ...recipient, selected: checked }))
    );
  };

  const sendMessages = async () => {
    try {
      setLoading(true);
      setActiveTab('sending');
      
      const selectedRecipients = recipients.filter(r => r.selected);
      const formValues = await form.validateFields();
      
      if (selectedRecipients.length === 0) {
        message.warning('Please select at least one recipient');
        return;
      }

      const results = {
        total: selectedRecipients.length,
        successful: 0,
        failed: 0,
        details: [],
        scheduled: isScheduled,
        scheduleTime: isScheduled ? moment(scheduleDate).hour(scheduleTime.hour()).minute(scheduleTime.minute()) : null
      };

      // Process each recipient
      for (const recipient of selectedRecipients) {
        try {
          let personalizedMessage = messageText;
          
          if (usePersonalization) {
            personalizedMessage = personalizedMessage
              .replace(/{site_name}/g, recipient.siteName)
              .replace(/{manager_name}/g, recipient.name)
              .replace(/{site_type}/g, recipient.siteType);
          }
          
          personalizedMessage = processMessageText(personalizedMessage);

          const messageData = {
            to: recipient.mobile,
            message: personalizedMessage,
            orgId: currentUser.orgId,
            userId: currentUser.uid,
            scheduled: isScheduled,
            scheduleTime: results.scheduleTime?.toISOString(),
            crisisId: crisisId,
            deliveryTracking: deliveryTracking
          };

          let response;
          if (isScheduled) {
            // Save to scheduled messages collection
            response = await addDoc(collection(db, 'c3_scheduled_messages'), {
              ...messageData,
              status: 'scheduled',
              createdAt: new Date()
            });
          } else {
            // Send immediately
            response = await fetch('/api/send-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...messageData,
                incidentId: crisisId || null
              })
            });
          }

          if ((isScheduled && response) || (!isScheduled && response.ok)) {
            results.successful++;
            results.details.push({
              recipient: recipient,
              status: 'success',
              message: isScheduled ? 'Scheduled successfully' : 'Sent successfully'
            });

            // Update recipient status
            setRecipients(prev => 
              prev.map(r => 
                r.id === recipient.id 
                  ? { ...r, deliveryStatus: isScheduled ? 'scheduled' : 'sent' }
                  : r
              )
            );
          } else {
            throw new Error(isScheduled ? 'Failed to schedule' : 'Failed to send');
          }

          // Log communication
          await logCommunication(recipient, personalizedMessage, 'success');

        } catch (error) {
          results.failed++;
          results.details.push({
            recipient: recipient,
            status: 'failed',
            message: error.message
          });

          setRecipients(prev => 
            prev.map(r => 
              r.id === recipient.id 
                ? { ...r, deliveryStatus: 'failed' }
                : r
            )
          );

          await logCommunication(recipient, messageText, 'failed');
        }
      }

      setSendResults(results);
      setActiveTab('results');

      if (onSendComplete) {
        onSendComplete(results);
      }

    } catch (error) {
      console.error('Error sending messages:', error);
      message.error('Failed to send messages');
    } finally {
      setLoading(false);
    }
  };

  const logCommunication = async (recipient, message, status) => {
    try {
      const logData = {
        type: 'sms_mass_contact_advanced',
        recipient_name: recipient.name,
        recipient_mobile: recipient.mobile,
        site_id: recipient.id,
        site_name: recipient.siteName,
        message: message,
        status: status,
        sent_by: currentUser.uid,
        sent_by_name: currentUser.displayName || currentUser.email,
        org_id: currentUser.orgId,
        crisis_id: crisisId,
        scheduled: isScheduled,
        schedule_time: isScheduled ? moment(scheduleDate).hour(scheduleTime.hour()).minute(scheduleTime.minute()).toISOString() : null,
        delivery_tracking: deliveryTracking,
        timestamp: new Date(),
        created_at: new Date()
      };

      await addDoc(collection(db, 'c3_messages'), logData);
    } catch (error) {
      console.error('Error logging communication:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setActiveTab('compose');
    setMessageText('');
    setSelectedTemplate(null);
    setIsScheduled(false);
    setScheduleDate(null);
    setScheduleTime(null);
    setSendResults(null);
    setRecipients([]);
    onClose();
  };

  const selectedCount = recipients.filter(r => r.selected).length;

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <span>Advanced SMS Composer</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={null}
      className="advanced-sms-composer"
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Compose Tab */}
        <TabPane 
          tab={
            <Space>
              <MessageOutlined />
              <span>Compose</span>
            </Space>
          } 
          key="compose"
        >
          <Form form={form} layout="vertical">
            {/* Message Templates */}
            <Card title="Message Templates" size="small" className="mb-3">
              <Space direction="vertical" style={{ width: '100%' }}>
                {Object.entries(DEFAULT_TEMPLATES).map(([categoryKey, category]) => (
                  <div key={categoryKey}>
                    <Text strong>{category.name}:</Text>
                    <div style={{ marginTop: 4, marginBottom: 8 }}>
                      {category.templates.map(template => (
                        <Button
                          key={template.id}
                          size="small"
                          type={selectedTemplate?.id === template.id ? 'primary' : 'default'}
                          onClick={() => handleTemplateSelect(template.id)}
                          style={{ margin: '2px', fontSize: '11px' }}
                        >
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </Space>
            </Card>

            {/* Message Composition */}
            <Form.Item 
              label="Message Content" 
              name="message"
              rules={[{ required: true, message: 'Please enter your message' }]}
            >
              <TextArea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter your SMS message here... Use {site_name}, {manager_name}, {timestamp} for personalization"
                rows={6}
                showCount
                maxLength={1600}
              />
            </Form.Item>

            {/* Message Options */}
            <Card title="Message Options" size="small" className="mb-3">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Use Personalization:</Text>
                  <Switch 
                    checked={usePersonalization}
                    onChange={setUsePersonalization}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Schedule Message:</Text>
                  <Switch 
                    checked={isScheduled}
                    onChange={setIsScheduled}
                  />
                </div>

                {isScheduled && (
                  <Space>
                    <DatePicker
                      placeholder="Select date"
                      value={scheduleDate}
                      onChange={setScheduleDate}
                      disabledDate={current => current && current < moment().startOf('day')}
                    />
                    <TimePicker
                      placeholder="Select time"
                      value={scheduleTime}
                      onChange={setScheduleTime}
                      format="HH:mm"
                    />
                  </Space>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Delivery Tracking:</Text>
                  <Switch 
                    checked={deliveryTracking}
                    onChange={setDeliveryTracking}
                  />
                </div>
              </Space>
            </Card>

            {/* Message Statistics */}
            <Card title="Message Statistics" size="small" className="mb-3">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Characters:</Text>
                  <Text>{characterCount}/1600</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>SMS Segments:</Text>
                  <Text>{Math.ceil(characterCount / 160)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Recipients:</Text>
                  <Text>{selectedCount}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Estimated Cost:</Text>
                  <Text>${estimatedCost.toFixed(2)}</Text>
                </div>
              </Space>
            </Card>

            {/* Action Buttons */}
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Button 
                  icon={<SaveOutlined />}
                  onClick={saveAsTemplate}
                  disabled={!messageText.trim()}
                >
                  Save Template
                </Button>
              </Space>
              
              <Space>
                <Button onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={isScheduled ? <ScheduleOutlined /> : <SendOutlined />}
                  onClick={() => setActiveTab('recipients')}
                  disabled={!messageText.trim() || selectedCount === 0}
                  style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                >
                  {isScheduled ? `Schedule (${selectedCount})` : `Send (${selectedCount})`}
                </Button>
              </Space>
            </Space>
          </Form>
        </TabPane>

        {/* Recipients Tab */}
        <TabPane 
          tab={
            <Space>
              <UserOutlined />
              <span>Recipients ({selectedCount})</span>
            </Space>
          } 
          key="recipients"
        >
          <Card
            title={`Review Recipients (${selectedCount} selected)`}
            extra={
              <Space>
                <Button 
                  size="small"
                  onClick={() => selectAllRecipients(true)}
                >
                  Select All
                </Button>
                <Button 
                  size="small"
                  onClick={() => selectAllRecipients(false)}
                >
                  Deselect All
                </Button>
              </Space>
            }
          >
            <List
              dataSource={recipients}
              renderItem={recipient => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <input
                        type="checkbox"
                        checked={recipient.selected}
                        onChange={(e) => handleRecipientToggle(recipient.id, e.target.checked)}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>{recipient.name}</Text>
                        <Tag size="small">{recipient.siteName}</Tag>
                        {recipient.deliveryStatus !== 'pending' && (
                          <Tag 
                            size="small" 
                            color={
                              recipient.deliveryStatus === 'sent' ? 'green' :
                              recipient.deliveryStatus === 'scheduled' ? 'blue' :
                              recipient.deliveryStatus === 'failed' ? 'red' : 'default'
                            }
                          >
                            {recipient.deliveryStatus}
                          </Tag>
                        )}
                      </Space>
                    }
                    description={
                      <Space>
                        <PhoneOutlined />
                        <Text type="secondary">{recipient.mobile}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            />
          </Card>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setActiveTab('compose')}>
              Back to Compose
            </Button>
            <Button
              type="primary"
              icon={isScheduled ? <ScheduleOutlined /> : <SendOutlined />}
              onClick={sendMessages}
              loading={loading}
              disabled={selectedCount === 0}
              style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c' }}
            >
              {isScheduled ? `Schedule ${selectedCount} Messages` : `Send ${selectedCount} Messages`}
            </Button>
          </div>
        </TabPane>

        {/* Sending Tab */}
        <TabPane 
          tab={
            <Space>
              <ClockCircleOutlined spin />
              <span>Sending...</span>
            </Space>
          } 
          key="sending"
          disabled={!loading}
        >
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ClockCircleOutlined style={{ fontSize: '48px', color: '#39325b', marginBottom: '16px' }} />
            <Title level={3}>
              {isScheduled ? 'Scheduling Messages...' : 'Sending Messages...'}
            </Title>
            <Text type="secondary">
              {isScheduled ? 
                'Your messages are being scheduled for delivery.' :
                'Please wait while your messages are being sent.'
              }
            </Text>
            <Progress 
              percent={Math.round((sendResults?.successful + sendResults?.failed || 0) / recipients.filter(r => r.selected).length * 100)}
              style={{ marginTop: '24px' }}
            />
          </div>
        </TabPane>

        {/* Results Tab */}
        <TabPane 
          tab={
            <Space>
              <CheckCircleOutlined />
              <span>Results</span>
            </Space>
          } 
          key="results"
          disabled={!sendResults}
        >
          {sendResults && (
            <Card
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>
                    {isScheduled ? 'Scheduling Complete' : 'Send Complete'}
                  </span>
                </Space>
              }
            >
              <div className="mb-3">
                <Space size="large">
                  <div>
                    <Text strong>{sendResults.total}</Text>
                    <br />
                    <Text type="secondary">Total</Text>
                  </div>
                  <div>
                    <Text strong style={{ color: '#52c41a' }}>{sendResults.successful}</Text>
                    <br />
                    <Text type="secondary">
                      {isScheduled ? 'Scheduled' : 'Successful'}
                    </Text>
                  </div>
                  <div>
                    <Text strong style={{ color: '#ff4d4f' }}>{sendResults.failed}</Text>
                    <br />
                    <Text type="secondary">Failed</Text>
                  </div>
                </Space>
              </div>

              {isScheduled && sendResults.scheduleTime && (
                <Alert
                  message={`Messages scheduled for ${sendResults.scheduleTime.format('DD/MM/YYYY HH:mm')}`}
                  type="info"
                  showIcon
                  className="mb-3"
                />
              )}

              <List
                dataSource={sendResults.details}
                renderItem={result => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        result.status === 'success' 
                          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          : <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                      }
                      title={
                        <Space>
                          <Text strong>{result.recipient.name}</Text>
                          <Text type="secondary">({result.recipient.siteName})</Text>
                        </Space>
                      }
                      description={
                        <div>
                          <Text type="secondary">{result.recipient.mobile}</Text>
                          <br />
                          <Text 
                            type={result.status === 'success' ? 'success' : 'danger'}
                            style={{ fontSize: '12px' }}
                          >
                            {result.message}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
                style={{ maxHeight: '300px', overflowY: 'auto' }}
              />

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button type="primary" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </Card>
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default AdvancedSMSComposer; 